// ============================================================
// Duyuru arayüzü (§13) — kurum ve veli tarafı
// ============================================================

import { card, emptyNote, sectionTitle } from "./ui.js";
import { avatar } from "../lib/avatar.js";
import { esc, fmtDate } from "../lib/fmt.js";
import { B, cls } from "../domain/context.js";
import { HEDEFLER, TURLER, aliciVeliSayisi, duyuruDurumu, duyurular, veliDuyurulari } from "../domain/announcements.js";
import { KANALLAR } from "../domain/notifications.js";
import { yapabilir } from "../domain/auth.js";

// ---------- Kurum tarafı ----------

export function duyuruSekmesi(branchId) {
  const list = duyurular(branchId);

  return `
  ${yapabilir("mesajYaz") ? `
    <div class="flex items-center justify-between gap-3 flex-wrap mb-4">
      <p class="text-sm text-inksoft">
        Sınıfa veya tüm kuruma duyuru gönderin; onay isteyen duyurular ve anketler yanıt takibi yapar.
      </p>
      <button data-newduyuru class="rounded-xl bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-inksoft transition">
        + Yeni duyuru
      </button>
    </div>` : ""}

  ${list.length ? `
    <div class="grid gap-4 items-start">
      ${list.map(d => duyuruKarti(d, branchId)).join("")}
    </div>`
  : emptyNote("Henüz duyuru yok.")}`;
}

function duyuruKarti(d, branchId) {
  const tur = TURLER[d.tur] ?? TURLER.duyuru;
  const durum = duyuruDurumu(d, branchId);
  const hedef = HEDEFLER[d.hedef];
  const hedefAdi = d.hedef === "sinif"
    ? `${cls(d.hedefParam)?.name ?? d.hedefParam} sınıfı`
    : hedef?.label ?? d.hedef;

  return card(`
    <div class="p-5">
      <div class="flex items-start gap-3">
        <span class="text-lg shrink-0">${tur.icon}</span>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="font-display font-bold truncate">${esc(d.baslik)}</h2>
            <span class="text-[10px] font-bold rounded-full bg-grape-soft text-grape px-2 py-0.5 shrink-0">${esc(hedefAdi)}</span>
            ${durum.suresiGecti
              ? `<span class="text-[10px] font-bold rounded-full bg-line text-inksoft px-2 py-0.5 shrink-0">Süresi doldu</span>`
              : d.deadline ? `<span class="text-[10px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2 py-0.5 shrink-0">Son ${fmtDate(d.deadline)}</span>` : ""}
          </div>
          <div class="text-[11px] text-inksoft mt-0.5">
            ${esc(d.createdBy)} · ${fmtDate(d.createdAt.slice(0, 10))} ·
            ${aliciVeliSayisi(d, branchId)} veliye gönderildi
            ${d.kanallar.map(k => KANALLAR[k]?.icon ?? "").join(" ")}
          </div>
        </div>
      </div>

      <p class="text-sm mt-3 leading-snug">${esc(d.metin)}</p>

      <!-- Okuma ve yanıt oranları -->
      <div class="grid grid-cols-2 gap-2 mt-4">
        ${oran("Okundu", durum.okuyan, durum.alici, durum.okumaOrani, "sky")}
        ${tur.onayIster || tur.anket
          ? oran(tur.anket ? "Yanıtlandı" : "Onaylandı", durum.yanitlayan, durum.alici, durum.yanitOrani, "teal")
          : `<div class="rounded-xl bg-paper p-3">
               <div class="text-[11px] text-inksoft">Yanıt istenmiyor</div>
             </div>`}
      </div>

      <!-- Anket / onay dağılımı -->
      ${Object.keys(durum.dagilim).length ? `
        <div class="mt-3">
          ${Object.entries(durum.dagilim).sort((a, b) => b[1] - a[1]).map(([secenek, sayi]) => {
            const oranPct = durum.yanitlayan ? Math.round((sayi / durum.yanitlayan) * 100) : 0;
            return `
            <div class="mb-2">
              <div class="flex items-center justify-between gap-2 text-xs">
                <span>${esc(secenek)}</span>
                <span class="shrink-0 font-semibold">${sayi} <span class="text-inksoft font-normal">%${oranPct}</span></span>
              </div>
              <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
                <div class="h-full rounded-full bg-teal" style="width:${oranPct}%"></div>
              </div>
            </div>`;
          }).join("")}
        </div>` : ""}

      <!-- Yanıt bekleyenler -->
      ${(tur.onayIster || tur.anket) && durum.yanitBekleyenler.length && !durum.suresiGecti ? `
        <div class="mt-3 pt-3 border-t border-line">
          <div class="text-[11px] font-bold text-inksoft uppercase mb-1.5">
            Yanıt bekleyen ${durum.yanitBekleyenler.length} veli
          </div>
          <div class="flex flex-wrap gap-1.5">
            ${durum.yanitBekleyenler.map(s => `
              <span data-student="${s.id}" class="inline-flex items-center gap-1.5 rounded-full bg-paper border border-line px-2 py-1 text-[11px] cursor-pointer hover:bg-line/40">
                ${avatar(s, "w-5 h-5")}<span>${esc(s.name)}</span>
              </span>`).join("")}
          </div>
          ${yapabilir("mesajYaz") ? `
            <button data-duyuruhatirlat="${d.id}"
              class="mt-2 rounded-lg bg-ink text-white text-[10px] font-semibold px-2.5 py-1 hover:bg-inksoft">
              Yanıt bekleyenlere hatırlat
            </button>` : ""}
        </div>` : ""}
    </div>`, "min-w-0");
}

const oran = (label, sayi, toplam, pct, renk) => `
  <div class="rounded-xl bg-paper p-3">
    <div class="flex items-center justify-between gap-2">
      <span class="text-[11px] text-inksoft">${label}</span>
      <span class="text-[11px] font-semibold">${sayi}/${toplam}</span>
    </div>
    <div class="h-1.5 rounded-full bg-line mt-1.5 overflow-hidden">
      <div class="h-full rounded-full bg-${renk}" style="width:${pct}%"></div>
    </div>
  </div>`;

// ---------- Veli tarafı ----------

export function veliDuyuruKarti(student) {
  const list = veliDuyurulari(student, B().id);
  if (!list.length) return "";

  const bekleyen = list.filter(d => d.yanitGerekli && !d.yanit?.answer);

  return card(`
    <div class="p-5">
      ${sectionTitle("Kurumdan Duyurular", bekleyen.length
        ? `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${bekleyen.length} yanıt bekliyor</span>`
        : "")}

      <div class="space-y-2">
        ${list.slice(0, 5).map(d => {
          const tur = TURLER[d.tur] ?? TURLER.duyuru;
          const yanitlandi = Boolean(d.yanit?.answer);
          return `
          <div class="rounded-xl border ${d.yanitGerekli && !yanitlandi ? "border-coral" : "border-line"} p-3">
            <div class="flex items-center gap-2">
              <span class="shrink-0">${tur.icon}</span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold truncate">${esc(d.baslik)}</span>
                <span class="block text-[11px] text-inksoft">
                  ${fmtDate(d.createdAt.slice(0, 10))}
                  ${d.deadline ? ` · son ${fmtDate(d.deadline)}` : ""}
                </span>
              </span>
              ${yanitlandi ? `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5 shrink-0">${esc(d.yanit.answer)}</span>` : ""}
            </div>

            <p class="text-xs mt-2 leading-snug">${esc(d.metin)}</p>

            ${d.yanitGerekli && !yanitlandi ? `
              <div class="flex items-center gap-1.5 mt-2 flex-wrap">
                ${d.secenekler.map(sec => `
                  <button data-duyuruyanit="${d.id}:${student.id}:${esc(sec)}"
                    class="rounded-lg bg-ink text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-inksoft">
                    ${esc(sec)}
                  </button>`).join("")}
              </div>` : ""}
          </div>`;
        }).join("")}
      </div>
    </div>`, "min-w-0");
}
